<script setup>
import { ref, inject, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import axios from 'axios'
import { isValidEmail } from '@/helpers/general.js'
import { useUserStore } from '@/stores/UserStore'

const router = useRouter()
const route = useRoute()
console.log('route', route)

const userStore = useUserStore()

const siteConfig = inject('siteConfig')

const userEmail = ref(null)
const userPassword = ref(null)
const errorMessage = ref(null)
const showPassword = ref(false)

const signIn = () => {
  if (
    !userEmail.value ||
    !userPassword.value ||
    !isValidEmail(userEmail.value)
  ) {
    errorMessage.value = 'Please enter a valid email and password'
    return
  }

  errorMessage.value = null
  axios
    .post(`${siteConfig.apiBaseUrl}/users/login`, {
      username: userEmail.value,
      password: userPassword.value,
    })
    .then((response) => {
      console.log('login response.data', response.data)
      const { ok, account, token } = response.data

      if (ok) {
        userStore.loggedIn = true
        userStore.firstName = account.firstName
        userStore.account = account
        userStore.token = token
        localStorage.setItem(
          `${siteConfig.key}Auth`,
          JSON.stringify({
            token,
            email: account.userName,
            name: account.firstName,
            expires: Date.now() + (60 * 60 * 1000) // 60 minutes from now in milliseconds
          })
        )
        userStore.fetchMembershipsForHousehold()
        userStore.fetchGroups()
        if (siteConfig.key === 'snowfarm') {
          userStore.fetchRegistrationFees()
        }

        if (
          siteConfig.features.requireCompleteProfile &&
          !userStore.hasCompleteProfile
        ) {
          router.push({ name: 'Account: Account Info' })
        } else if (route.query.backTo) {
          const [path, search] = route.query.backTo.split('?')
          const queryParams = Object.fromEntries(
            new URLSearchParams(search)
          )
          router.push({ path, query: queryParams })
        } else {
          router.push({ name: 'Store Classes' })
        }
      } else {
        const message = response?.data?.error
        if (message) {
          errorMessage.value = message
        } else {
          errorMessage.value = 'An error has occurred. Please try again later.'
        }
      }
    })
    .catch((error) => {
      console.error('login error', error)
      console.error('login error.response', error.response)
      const message = error?.response?.data?.error
      if (message) {
        errorMessage.value = message
      } else {
        errorMessage.value = 'An error has occurred. Please try again later.'
      }
    })
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}
</script>

<template>
  <section class="store-sign-in">
    <div class="xl:container">
      <div class="row">
        <div class="form-column">
          <form @submit.prevent>
            <h1 class="page-heading">Sign In</h1>
            <p
              class="page-description small-regular"
              v-if="siteConfig.key === 'snowfarm'"
            >
              Sign in to register for programs, make donations, and more.
            </p>
            <p v-else class="page-description small-regular">
              Sign in to register for programs, purchase memberships, and more.
            </p>
            <p
              class="page-description small-regular"
              v-if="siteConfig.features.showNewSystemPasswordResetBlurb"
            >
              If this is your first time logging in to our new system, please
              <router-link
                :to="{ name: 'Forgot Password' }"
                class="reset-link small-bold text-accent-1"
              >
                reset your password
              </router-link>.
            </p>

            <p
              v-if="errorMessage"
              class="page-description small-regular font-semibold error"
            >
              <span>{{ errorMessage }}</span>
            </p>

            <label class="input-icon-component">
              <strong>Your email</strong>
              <div class="inp-box">
                <input
                  id="user-email"
                  type="email"
                  placeholder="name@example.com"
                  class="form-control"
                  v-model="userEmail"
                  required
                />
                <Icon icon="fluent:mail-12-filled" class="icon" />
              </div>
            </label>

            <label class="input-icon-component">
              <strong>Password</strong>
              <div class="inp-box">
                <input
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="••••••••••"
                  class="form-control"
                  v-model="userPassword"
                  required
                />
                <KeyIcon class="icon" />
                <button
                  type="button"
                  class="password-toggle"
                  @click="togglePasswordVisibility"
                >
                  <EyeIcon v-if="!showPassword" class="icon" />
                  <EyeSlashIcon v-else class="icon" />
                </button>
              </div>
            </label>

            <div class="form-helpers">
              <router-link
                :to="{ name: 'Forgot Password' }"
                class="reset-link small-bold"
              >
                Forgot password?
              </router-link>
            </div>

            <button class="btn-submit btn-accent-1" @click="signIn">
              Sign In
            </button>
          </form>

          <router-link
            :to="{ name: 'Create: Account Info' }"
            class="register-link small-regular"
          >
            Don't have an account yet?
            <strong>Create an Account</strong>
          </router-link>
        </div>
        <div class="bg-column">
          <img
            :src="`/sites/${siteConfig.key}/${siteConfig.assets.signInImage}`"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@import url('./index.pcss');

.inp-box {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
}

.password-toggle .icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
}
</style>
